import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { CatIcon, CITIES, districtsFor, MATERIAL_OPTIONS } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, MapPin, ArrowRight, Loader2, CheckCircle2, Camera, X, Lock } from "lucide-react";
import { toast } from "sonner";

const DRAFT_KEY = "ustayla_job_draft";

export default function PostJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    return saved ? JSON.parse(saved) : {
      city: "", district: "", category_slug: "", title: "", description: "",
      area_m2: "", material_status: "", budget_min: "", budget_max: "", start_time: "", photos: [],
    };
  });

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);
  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedCat = categories.find((c) => c.slug === form.category_slug);

  const addPhoto = () => {
    if (form.photos.length >= 5) { toast.error("En fazla 5 fotoğraf ekleyebilirsiniz"); return; }
    const url = prompt("Fotoğraf URL'si girin (demo):", "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80");
    if (url) upd("photos", [...form.photos, url]);
  };

  const submit = async () => {
    if (!user || !user.id) { setAuthOpen(true); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      };
      const { data } = await api.post("/jobs", payload);
      localStorage.removeItem(DRAFT_KEY);
      setCreated(data);
      toast.success("İlanınız yayınlandı!");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  if (created) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center animate-fade-up">
        <div className="h-20 w-20 rounded-full tinted flex items-center justify-center mx-auto text-emerald-600">
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900 mt-6">İş ilanın oluşturuldu!</h1>
        <p className="text-slate-600 mt-2">Artık sana en uygun ustalar kısa sürede tekliflerini iletecek.</p>
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mt-8 text-left space-y-2">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><MapPin className="h-4 w-4" /> {created.city} / {created.district}</div>
          <div className="font-bold text-slate-900 text-lg">{created.title}</div>
          <div className="text-sm text-slate-500">Kategori: {created.category_name}</div>
          <div className="text-sm text-slate-500">İlan No: <span className="font-mono">{created.id}</span></div>
          <div className="text-sm"><span className="text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full text-xs">Aktif</span></div>
        </div>
        <div className="flex flex-col gap-3 mt-6">
          <Button onClick={() => navigate(`/ilan/${created.id}`)} className="brand-gradient text-white rounded-xl h-11" data-testid="view-created-job">İlanı Görüntüle →</Button>
          <Button variant="outline" onClick={() => navigate("/")} className="rounded-xl h-11">Ana Sayfaya Dön</Button>
        </div>
      </div>
    );
  }

  const canNext1 = form.city && form.district;
  const canNext2 = form.category_slug;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3].map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${step >= s ? "brand-gradient text-white" : "bg-slate-200 text-slate-500"}`}>
              {step > s ? <Check className="h-4 w-4" /> : s}
            </div>
            <span className={`ml-2 text-sm font-semibold hidden sm:block ${step >= s ? "text-slate-900" : "text-slate-400"}`}>
              {s === 1 ? "Konum" : s === 2 ? "Hizmet" : "Detaylar"}
            </span>
            {i < 2 && <div className={`w-10 sm:w-16 h-0.5 mx-2 ${step > s ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8">
        {step === 1 && (
          <div className="animate-fade-up">
            <h2 className="text-2xl font-extrabold text-slate-900 text-center">Şehir ve ilçeni seç</h2>
            <p className="text-slate-500 text-center text-sm mt-1">İşin yapılacağı konumu belirt, sana en yakın ustalar görelim.</p>
            <div className="space-y-4 mt-6">
              <Select value={form.city} onValueChange={(v) => { upd("city", v); upd("district", ""); }}>
                <SelectTrigger className="rounded-xl h-12" data-testid="wizard-city-select"><SelectValue placeholder="Şehir seçin" /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.district} onValueChange={(v) => upd("district", v)} disabled={!form.city}>
                <SelectTrigger className="rounded-xl h-12" data-testid="wizard-district-select"><SelectValue placeholder="İlçe seçin" /></SelectTrigger>
                <SelectContent>{districtsFor(form.city).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Button disabled={!canNext1} onClick={() => setStep(2)} className="w-full brand-gradient text-white rounded-xl h-12 font-semibold" data-testid="job-wizard-step-1-next">
                Devam Et <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <h2 className="text-2xl font-extrabold text-slate-900 text-center">Ne yaptırmak istiyorsunuz?</h2>
            <p className="text-slate-500 text-center text-sm mt-1">İhtiyacına en uygun kategoriyi seç.</p>
            <div className="grid grid-cols-3 gap-3 mt-6">
              {categories.map((c) => (
                <button key={c.slug} onClick={() => upd("category_slug", c.slug)} data-testid={`wizard-cat-${c.slug}`}
                  className={`p-4 rounded-2xl border flex flex-col items-center gap-2 transition-colors ${form.category_slug === c.slug ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}>
                  <CatIcon name={c.icon} className="h-6 w-6 text-emerald-600" />
                  <span className="text-xs font-semibold text-slate-700 text-center leading-tight">{c.name}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl h-12 flex-1">Geri</Button>
              <Button disabled={!canNext2} onClick={() => setStep(3)} className="flex-1 brand-gradient text-white rounded-xl h-12 font-semibold" data-testid="job-wizard-step-2-next">
                Devam Et <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-emerald-700 font-semibold"><MapPin className="h-4 w-4" /> {form.city} / {form.district}</span>
              <span className="flex items-center gap-1.5 text-slate-600">{selectedCat?.name}</span>
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900">İşin detaylarını paylaş</h2>
            <div>
              <Input value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="İlan başlığı (örn. Salon boya badana)" className="rounded-xl h-12" data-testid="wizard-title-input" />
            </div>
            <div>
              <Textarea value={form.description} maxLength={500} onChange={(e) => upd("description", e.target.value)}
                placeholder="Örn: 10 m² banyo tadilatı, seramik değişecek, tesisat yenilenecek..." className="rounded-xl min-h-28" data-testid="wizard-description-input" />
              <div className="text-right text-xs text-slate-400 mt-1">{form.description.length}/500</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" value={form.area_m2} onChange={(e) => upd("area_m2", e.target.value.replace(/[^0-9]/g, ""))} placeholder="Ölçü / m² (örn. 100)" className="rounded-xl h-11" data-testid="wizard-area-input" />
              <Select value={form.material_status} onValueChange={(v) => upd("material_status", v)}>
                <SelectTrigger className="rounded-xl h-11" data-testid="wizard-material-select"><SelectValue placeholder="Malzeme durumu" /></SelectTrigger>
                <SelectContent>{MATERIAL_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={form.budget_min} onChange={(e) => upd("budget_min", e.target.value)} onBlur={(e) => upd("budget_min", e.target.value ? String(Math.max(500, Math.round(Number(e.target.value) / 500) * 500)) : "")} placeholder="Min bütçe (TL)" className="rounded-xl h-11" data-testid="wizard-budget-min" />
              <Input type="number" value={form.budget_max} onChange={(e) => upd("budget_max", e.target.value)} onBlur={(e) => upd("budget_max", e.target.value ? String(Math.max(500, Math.round(Number(e.target.value) / 500) * 500)) : "")} placeholder="Max bütçe (TL)" className="rounded-xl h-11" data-testid="wizard-budget-max" />
            </div>
            <Select value={form.start_time} onValueChange={(v) => upd("start_time", v)}>
              <SelectTrigger className="rounded-xl h-11" data-testid="wizard-start-select"><SelectValue placeholder="İşe başlama zamanı" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Hemen">Hemen</SelectItem>
                <SelectItem value="1 hafta içinde">1 hafta içinde</SelectItem>
                <SelectItem value="Esnek">Esnek</SelectItem>
              </SelectContent>
            </Select>

            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Fotoğraflar (isteğe bağlı)</div>
              <div className="flex flex-wrap gap-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative h-20 w-20 rounded-xl overflow-hidden">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => upd("photos", form.photos.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-black/50 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {form.photos.length < 5 && (
                  <button onClick={addPhoto} data-testid="wizard-add-photo" className="h-20 w-20 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-emerald-400">
                    <Camera className="h-5 w-5" /><span className="text-[10px] mt-1">Ekle</span>
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">En fazla 5 fotoğraf yükleyebilirsin.</p>
            </div>

            <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-xl p-3">
              <Lock className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              Adres ve iletişim bilginiz gizli tutulur. İlanda yalnızca şehir/ilçe görünür.
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl h-12 flex-1">Geri</Button>
              <Button disabled={!form.title || !form.description || submitting} onClick={submit} className="flex-1 brand-gradient text-white rounded-xl h-12 font-semibold" data-testid="job-wizard-submit-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "İlanı Oluştur"}
              </Button>
            </div>
          </div>
        )}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} onSuccess={submit} />
    </div>
  );
}
